<template>
    <DrawerAccordion id="corp" :title="$t.__('overview.companyBlock.title')">
        <template #action>
            <CompassPlainButton
                v-if="showEditButton"
                :label="$t.__('common.actions.edit')"
                size="small"
                @click.stop="handleEdit"
            />
        </template>
        <div class="overview-company-block">
            <div class="overview-company-block__company">
                <CompassTooltip
                    :content="$t.__('common.actions.see_the_file')"
                    :arrow="false"
                    placement="right-start"
                >
                    <RouterLink :to="companyLink">
                        <CompassObject size="extra-small" :label="company.name" :isLink="true" />
                    </RouterLink>
                </CompassTooltip>
            </div>
            <CompassDescriptionList variant="frozen-field">
                <CompassDescriptionListItem
                    v-if="company.email"
                    :label="$t.__('common.labels.email')"
                >
                    <EmailLinkCompass
                        :email="company.email"
                        :linkedid="linkedid"
                        :linkedtype="linkedtype"
                    >
                        {{ company.email }}
                    </EmailLinkCompass>
                </CompassDescriptionListItem>
                <CompassDescriptionListItem
                    v-if="company.phone"
                    :label="$t.__('common.labels.phone')"
                >
                    <PhoneLinkCompass :phoneNumber="company.phone" />
                </CompassDescriptionListItem>
                <CompassDescriptionListItem
                    v-if="Object.keys(address).length"
                    :label="$t.__('common.labels.address')"
                >
                    <CompassSkeleton v-if="isLoading" variant="line" :lines="2" />
                    <div
                        v-if="!isLoading"
                        class="typographies-paragraph-medium-regular overview-company-block__address"
                        v-html="formattedAddress"
                    />
                </CompassDescriptionListItem>
            </CompassDescriptionList>
        </div>
    </DrawerAccordion>
</template>
<script>
import CompaniesService from 'api/companies';
import { getIndividualAddresses } from 'api/individuals';
import EmailLinkCompass from 'vcomponents/overview/tabs/data/EmailLinkCompass.vue';
import PhoneLinkCompass from 'vcomponents/overview/tabs/data/PhoneLinkCompass.vue';
import DrawerAccordion from 'vcomponents/common/drawer/accordion/DrawerAccordion.vue';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters } = createNamespacedHelpers('user');

export default {
    components: {
        EmailLinkCompass,
        PhoneLinkCompass,
        DrawerAccordion,
    },
    props: {
        linkedtype: {
            type: String,
            required: true,
        },
        linkedid: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            address: {},
            isLoading: true,
        };
    },
    computed: {
        ...mapGetters({
            permissions: 'getPermissions',
        }),
        companyLink() {
            if (this.linkedtype === 'third') {
                return this.$store.state.third.identity.link;
            }
            return this.company.link;
        },
        company() {
            if (this.linkedtype === 'third') {
                return this.cleanCompanyDetails(this.$store.state.third.dataFetch.informations);
            }
            return this.cleanCompanyDetails(this.$store.state.opportunity.dataFetch.third);
        },
        formattedAddress() {
            if (Object.keys(this.address).length === 0) {
                return '';
            }

            return `${this.address.address_line_1}<br />
                ${this.addLineBreak(this.address.address_line_2)}
                ${this.addLineBreak(this.address.address_line_3)}
                ${this.addLineBreak(this.address.address_line_4)}
                ${this.address.postal_code} ${this.address.city}`;
        },
        id() {
            if (this.linkedtype === 'third') {
                return this.linkedid;
            }
            return this.company.id;
        },
        isIndividual() {
            return (
                (this.linkedtype === 'third' &&
                    this.$store.state.third.identity.isCorporation === false) ||
                (this.linkedtype === 'opportunity' && this.company.type === 'person')
            );
        },
        isSupplier() {
            return (
                this.linkedtype === 'third' && this.$store.state.third.identity.type === 'supplier'
            );
        },
        isProspect() {
            return (
                this.linkedtype === 'third' && this.$store.state.third.identity.type === 'prospect'
            );
        },
        showEditButton() {
            const permissionMap = {
                isIndividual: 'update_individuals',
                isSupplier: 'update_suppliers',
                isProspect: 'update_prospects',
                default: 'update_clients',
            };

            for (const [key, value] of Object.entries(permissionMap)) {
                if (key !== 'default' && this[key]) {
                    return this.permissions.acl[value] || false;
                }
            }

            return this.permissions.acl[permissionMap.default] || false;
        },
    },
    created() {
        if (this.isIndividual) {
            getIndividualAddresses(this.id)
                .then((response) => {
                    if (response.data.data.length) {
                        this.address =
                            response.data.data.find((item) => item.is_invoicing_address) ?? {};
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        } else {
            CompaniesService.getCompanyAddresses(this.id)
                .then((response) => {
                    if (response.data.data.length) {
                        this.address =
                            response.data.data.find((item) => item.is_invoicing_address) ?? {};
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },
    methods: {
        addLineBreak(addressLine) {
            return addressLine ? `${addressLine}<br />` : '';
        },
        cleanCompanyDetails(company) {
            if (!company) {
                return {};
            }

            return Object.fromEntries(company.map((item) => [item.key, item.value]));
        },
        handleEdit() {
            Third.edit(this.id);
        },
    },
};
</script>
<style lang="scss" scoped>
.overview-company-block {
    &__company {
        margin: 0 0 var(--spacing-2);
    }

    &__address {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &::v-deep {
        .shareUrlBtn {
            font-weight: var(--font-weights-proxima-nova-0);
        }

        .compass-icon-button {
            flex-shrink: 0;
        }

        .compass-description-list__item {
            min-width: 0; // stops overflow
        }

        .compass-description-list__item__content {
            min-height: 20px;
        }

        .copy-element {
            display: block;
            max-width: 100%;
        }

        .compass-link {
            max-width: calc(100% - 24px);

            &__label {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
            }
        }
    }
}
</style>
